import { useMemo, useState } from 'react';
import { t, useTheme } from '@superset-ui/core';
import { Input, List, Space, Tag, Button } from 'antd';
import Modal from 'src/components/Modal';
import { Switch } from 'src/components/Switch';
import { persistOverrides, clearOverrides } from './featureFlagSessionStorage';

interface FeatureFlagModalProps {
  show: boolean;
  onHide: () => void;
}

export default function FeatureFlagModal({
  show,
  onHide,
}: FeatureFlagModalProps) {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [overrides, setOverrides] = useState<Record<string, boolean>>(() => {
    const raw = window.featureFlagOverrides || {};
    const boolOnly: Record<string, boolean> = {};
    Object.entries(raw).forEach(([key, val]) => {
      if (typeof val === 'boolean') {
        boolOnly[key] = val;
      }
    });
    return boolOnly;
  });
  const [hasChanges, setHasChanges] = useState(false);

  const allFlags = useMemo(() => {
    const defaults = window.featureFlags || {};
    const merged: Record<string, boolean> = {};
    Object.entries(defaults).forEach(([key, val]) => {
      if (typeof val === 'boolean') {
        merged[key] = val;
      }
    });
    Object.entries(overrides).forEach(([key, val]) => {
      if (!(key in merged)) {
        merged[key] = val;
      }
    });
    return Object.keys(merged).sort();
  }, [overrides]);

  const filteredFlags = useMemo(
    () =>
      allFlags.filter(flag =>
        flag.toLowerCase().includes(search.trim().toLowerCase()),
      ),
    [allFlags, search],
  );

  const overrideCount = Object.keys(overrides).length;

  function getEffectiveValue(flag: string): boolean {
    if (flag in overrides) return overrides[flag];
    const val = window.featureFlags?.[flag as keyof typeof window.featureFlags];
    return typeof val === 'boolean' ? val : false;
  }

  function isOverridden(flag: string): boolean {
    return flag in overrides;
  }

  function handleToggle(flag: string, checked: boolean) {
    const raw = window.featureFlags?.[flag as keyof typeof window.featureFlags];
    const defaultValue = typeof raw === 'boolean' ? raw : false;
    const newOverrides = { ...overrides };
    if (checked === defaultValue) {
      delete newOverrides[flag];
    } else {
      newOverrides[flag] = checked;
    }
    setOverrides(newOverrides);
    persistOverrides(newOverrides);
    setHasChanges(true);
  }

  function handleReset() {
    setOverrides({});
    clearOverrides();
    setHasChanges(true);
  }

  function handleApplyAndReload() {
    window.location.reload();
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      title={t('Feature Flags')}
      primaryButtonName={t('Apply & Reload')}
      onHandledPrimaryAction={handleApplyAndReload}
      disablePrimaryButton={!hasChanges}
      width="600px"
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Input.Search
            placeholder={t('Search flags...')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
            style={{ width: 300 }}
          />
          <Space>
            {overrideCount > 0 && (
              <Tag color="blue">{t('%s overridden', overrideCount)}</Tag>
            )}
            <Button
              size="small"
              onClick={handleReset}
              disabled={overrideCount === 0}
            >
              {t('Reset All')}
            </Button>
          </Space>
        </Space>

        <List
          size="small"
          bordered
          dataSource={filteredFlags}
          style={{ maxHeight: 400, overflow: 'auto' }}
          locale={{ emptyText: t('No flags match your search') }}
          renderItem={flag => (
            <List.Item
              actions={[
                <Switch
                  key="toggle"
                  checked={getEffectiveValue(flag)}
                  onChange={(checked: boolean) => handleToggle(flag, checked)}
                  size="small"
                />,
              ]}
            >
              <Space>
                {isOverridden(flag) && <Tag color="blue">{t('modified')}</Tag>}
                <span style={{ fontFamily: 'monospace' }}>{flag}</span>
              </Space>
            </List.Item>
          )}
        />

        <span
          style={{
            fontSize: 12,
            color: theme.colors.grayscale.light1,
            fontStyle: 'italic',
          }}
        >
          {t(
            'A page reload is required for flag changes to take effect. Overrides persist until you close this browser tab.',
          )}
        </span>
      </Space>
    </Modal>
  );
}
